import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home, ShoppingCart } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
    
    // Send to Debug Panel
    try {
      const { logComponentCrash } = require('@/utils/debugTracker');
      logComponentCrash(error, errorInfo, this.props.name || 'Page');
    } catch (_) {}
    
    // Check if error is cart-related and offer to clear cart
    const errorString = error?.toString() || '';
    const isCartError = errorString.includes('cart') || 
                        errorString.includes('localStorage') ||
                        errorString.includes('JSON');
    
    if (isCartError) {
      console.warn('Cart-related error detected. Consider clearing cart data.');
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleClearCartAndRefresh = () => {
    try {
      localStorage.removeItem('sparePartsCart');
      console.log('Cart cleared successfully');
    } catch (e) {
      console.error('Failed to clear cart:', e);
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h1>
            
            <p className="text-gray-600 mb-6">
              We're sorry, but something unexpected happened. Please try one of the options below.
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={this.handleRefresh}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
              
              <Button 
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Home
              </Button>
              
              <Button 
                onClick={this.handleClearCartAndRefresh}
                variant="outline"
                className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Clear Cart & Refresh
              </Button>
            </div>
            
            <p className="text-xs text-gray-400 mt-4">
              If the problem persists, please contact support.
            </p>
            
            {/* Debug info (hidden by default, can be shown for debugging) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left text-xs">
                <summary className="cursor-pointer text-gray-500">Error Details</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-40 text-red-600">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
